// Feature 3: the "what is this token?" dialogue, raised when the initiative turn
// lands on a token the tracker can't place (Initiative.tsx sets `ask` after an
// async statblock check). Modelled on RideKeeper's floating strip. Five answers,
// each routed to a mechanism that already exists:
//   • A creature…       — search the monster catalogue and RENAME the row to it
//                          (name is the tracker's join key, so it then resolves)
//   • Refresh catalogue — re-sync D&D Beyond so a just-added creature appears
//   • Effect timer      — convert the row into a short ticking effect in the order
//   • Event countdown   — convert it into a longer plot countdown
//   • Not a creature    — remember a "don't ask again" for this token (campaign)
import { useEffect, useMemo, useState } from "react";
import { newRequestId, request } from "../bridge";
import { loadCatalog, saveCatalog, type MonsterCatalog, type MonsterRecord } from "../state/catalogCache";
import type { MonsterCatalogResult } from "../state/laneResults";
import { useStore } from "../state/store";
import { withUnknownDismissed } from "../state/unknownToken";

type ExtRow = { id: string; name: string; [k: string]: unknown };
export interface UnknownAsk { id: string; name: string; tokenId?: string; key: string }

export function UnknownTokenAsk({ ask, onDone }: { ask: UnknownAsk | null; onDone: () => void }) {
  const { profile, update, logActivity } = useStore();
  const [mode, setMode] = useState<"" | "find" | "timer">("");
  const [q, setQ] = useState("");
  const [catalog, setCatalog] = useState<MonsterCatalog | null>(null);
  const [busy, setBusy] = useState("");
  const [rounds, setRounds] = useState(3);
  const [timerKind, setTimerKind] = useState<"effect" | "countdown">("effect");

  useEffect(() => { if (ask) { setMode(""); setQ(""); setBusy(""); loadCatalog(profile.id).then(setCatalog); } }, [ask?.id, profile.id]);

  if (!ask) return null;

  const patchRow = (patch: Partial<ExtRow>) =>
    update((l) => ({ ...l, externalInitiative: ((l.externalInitiative || []) as ExtRow[]).map((e) => (e.id === ask.id ? { ...e, ...patch } : e)) }));

  // Rename the row to a chosen creature. The whole tracker joins a row to its
  // statblock/vitals by name (bareName), so a correct name is the association.
  const associate = (name: string) => {
    patchRow({ name });
    logActivity({ text: `${ask.name || "Unknown token"} → ${name}`, meta: "identified from the monster catalogue", tone: "plain" });
    onDone();
  };

  const refresh = async () => {
    setBusy("Refreshing the monster catalogue…");
    try {
      const r = await request<MonsterCatalogResult>({ type: "DM_SCREEN_MONSTER_CATALOG_REQUEST", requestId: newRequestId("monster-catalog") }, { resultType: "DM_SCREEN_MONSTER_CATALOG_RESULT", timeoutMs: 15 * 60 * 1000 });
      if (!r.ok) throw new Error(r.error || "The catalogue read failed");
      const next: MonsterCatalog = { items: (r.items as MonsterRecord[]) || [], syncedAt: r.syncedAt || new Date().toISOString(), partial: Boolean(r.partial), officialCount: Number(r.officialCount || 0), homebrewCount: Number(r.homebrewCount || 0), accountFingerprint: r.accountFingerprint };
      await saveCatalog(profile.id, next);
      setCatalog(next); setMode("find"); setBusy("");
    } catch (e) { setBusy(e instanceof Error ? e.message : "Catalogue refresh failed"); }
  };

  // Convert the row into a timer in the order: it stops being an unplaceable
  // creature and ticks down on its own turn (the existing timer machinery).
  const makeTimer = () => {
    const n = Math.max(1, Math.min(99, Number(rounds) || 1));
    const label = timerKind === "effect" ? `⏱ ${ask.name}` : `⏳ ${ask.name}`;
    patchRow({ name: label, custom: true, source: "timer", timerRounds: n, timerRemaining: n, timerUnit: "rounds" });
    logActivity({ text: `${ask.name} set as ${timerKind === "effect" ? "an effect" : "a countdown"} timer`, meta: `${n} round${n === 1 ? "" : "s"}`, tone: "plain" });
    onDone();
  };

  const dismiss = () => { update((l) => withUnknownDismissed(l, ask.key, true)); onDone(); };

  const found = useMemo(() => {
    const text = q.trim().toLowerCase();
    return (catalog?.items || []).filter((m) => !text || `${m.name} ${m.type} ${m.source}`.toLowerCase().includes(text)).slice(0, 8);
  }, [catalog, q]);

  return (
    <div className="unknown-ask apply-hit" role="status">
      <div className="unknown-ask-head"><b>{ask.name || "A token"}</b> took a turn, but it isn't a creature the tracker knows. What is it?</div>
      {busy && <div className="unknown-ask-busy small muted">{busy}</div>}
      {mode === "" && (
        <div className="unknown-ask-actions">
          <button className="small primary" onClick={() => setMode("find")}>🔍 A creature…</button>
          <button className="small" onClick={() => void refresh()} disabled={!!busy}>↻ Refresh catalogue</button>
          <button className="small" onClick={() => { setTimerKind("effect"); setRounds(1); setMode("timer"); }}>⏱ Effect timer</button>
          <button className="small" onClick={() => { setTimerKind("countdown"); setRounds(3); setMode("timer"); }}>⏳ Event countdown</button>
          <button className="ghost small" onClick={dismiss}>✕ Not a creature — don't ask again</button>
        </div>
      )}
      {mode === "find" && (
        <div className="unknown-ask-find">
          <input className="xs" autoFocus placeholder="Search creatures…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the monster catalogue" />
          <div className="unknown-ask-matches">
            {found.length
              ? found.map((m) => <button key={m.id} className="small" onClick={() => associate(m.name)} title={`${m.type} · CR ${m.cr} · ${m.source}`}>{m.name} <span className="muted">CR {m.cr}</span></button>)
              : <span className="small muted">{catalog ? "No match — ↻ Refresh the catalogue?" : "Loading catalogue…"}</span>}
          </div>
          <button className="ghost small" onClick={() => setMode("")}>← Back</button>
        </div>
      )}
      {mode === "timer" && (
        <div className="unknown-ask-timer">
          <label className="small">{timerKind === "effect" ? "Effect" : "Countdown"} lasts <input className="xs" type="number" min={1} max={99} value={rounds} onChange={(e) => setRounds(Number(e.target.value))} aria-label="Duration in rounds" /> rounds</label>
          <button className="small primary" onClick={makeTimer}>Set timer</button>
          <button className="ghost small" onClick={() => setMode("")}>← Back</button>
        </div>
      )}
    </div>
  );
}
